const PRESENTATIONS = new Set(['happy', 'thoughtful', 'concerned', 'voila']);
const XR_DOMAINS = new Set(['prj', 'hr', 'crm', 'address']);

function normalizePresentation(value) {
	const normalized = String(value || '').trim().toLowerCase();
	return PRESENTATIONS.has(normalized) ? normalized : 'none';
}

function normalizeSource(message) {
	const source = String((message && message.answerSource) || '').trim().toLowerCase();
	// The explicit server-side routing result is authoritative. An XR answer may
	// also use Online Help for enrichment; helpUsed must not hide Leyla then.
	if (source === 'online-help' || source === 'synapcus-xr') return source;
	// Backward compatibility for persisted messages created before answerSource.
	if (!source && message && message.helpUsed) return 'online-help';
	return 'general';
}

function normalizeDomain(value) {
	const domain = String(value || '').trim().toLowerCase();
	return XR_DOMAINS.has(domain) ? domain : 'general';
}

function permitsVoilaEnding({messages, messageIndex, cooldown}) {
	const safeCooldown = Math.min(10, Math.max(2, Number(cooldown) || 3));
	const prior = (Array.isArray(messages) ? messages : [])
		.slice(0, Math.max(0, messageIndex))
		.filter(item => item && !item.self && item.answerSource === 'synapcus-xr');
	let sinceLastVoila = safeCooldown + 1;
	for (let index = prior.length - 1; index >= 0; index -= 1) {
		if (normalizePresentation(prior[index].assistantPresentation) === 'voila') {
			sinceLastVoila = prior.length - index;
			break;
		}
	}
	return sinceLastVoila > safeCooldown;
}

/**
 * Pure presentation dispatcher. It never influences tool routing, permissions,
 * XR calls or answer content. Unknown metadata always resolves to Synapcus S.
 */
export function dispatchAssistantAvatar({message, messageIndex = -1, messages = [], enabled = true, cooldown = 3}) {
	const fallback = {assetKey: 'synapcus', tooltipKey: 'synapcus', endingMoment: null};
	if (!enabled || !message || message.self) return fallback;

	const source = normalizeSource(message);
	if (source === 'online-help') {
		return {assetKey: 'synapcus', tooltipKey: 'onlineHelp', endingMoment: null};
	}
	if (source !== 'synapcus-xr') return fallback;

	const domain = normalizeDomain(message.assistantDomain);
	const presentation = normalizePresentation(message.assistantPresentation);
	let assetKey = domain;
	// Domain clothing must never be replaced by an unrelated role costume.
	// Sentiment portraits are used for generic XR answers; domain portraits
	// carry their own role-appropriate expression and the voila outro remains
	// available for exceptional complex answers.
	if (domain === 'general' && presentation === 'concerned') assetKey = 'concerned';
	else if (domain === 'general' && presentation === 'thoughtful') assetKey = 'thoughtful';
	else if (domain === 'general' && presentation === 'happy') assetKey = 'happy';
	else if (domain === 'general') assetKey = 'decisive';

	return {
		assetKey,
		tooltipKey: domain === 'general' ? 'general' : domain,
		endingMoment: presentation === 'voila' && permitsVoilaEnding({messages, messageIndex, cooldown})
			? 'voila'
			: null
	};
}

export function resolveAssistantAvatarLabel({language = '', key = 'synapcus', overrides = {}}) {
	const locale = String(language || '').toLowerCase();
	const lang = locale.split(/[-_]/)[0];
	const languageOverrides = overrides && typeof overrides[locale] === 'object'
		? overrides[locale]
		: overrides && typeof overrides[lang] === 'object'
			? overrides[lang]
			: overrides;
	return String((languageOverrides && (languageOverrides[key] || languageOverrides.synapcus)) || '');
}
